/*
=========================================================
* RingOperator Business Page — free public webpage, Dispatch design
=========================================================
*/

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import { brandPrimary, brandCream, brandAccent, brandCard } from "pages/RingOperatorHomepage/theme";

const line = "rgba(14,13,11,0.1)";
const muted = "rgba(14,13,11,0.55)";

const items = [
  {
    emoji: "📆",
    title: "Online Booking",
    body: "Customers book appointments straight from your page — same live availability the AI uses on the phone, with an instant SMS confirmation. No double-booking, no phone tag.",
  },
  {
    emoji: "⏳",
    title: "Waitlist & Queue",
    body: "Fully booked? Customers join your waitlist online or by phone, check their live place in line, and get texted automatically the moment a slot opens up.",
  },
  {
    emoji: "📝",
    title: "Feedback Capture",
    body: "A feedback form on your page catches complaints before they become public reviews — classified by severity, with instant alerts when something's critical.",
  },
  {
    emoji: "📱",
    title: "Printable QR Poster",
    body: "One click makes a print-ready QR poster for your counter, van, or invoices. Customers scan to book or join the waitlist — without tying up your phone.",
  },
];

function BusinessPage() {
  return (
    <MKBox
      component="section"
      id="webpage"
      className="ro-root"
      sx={{ py: { xs: 8, md: 12 }, background: brandCream }}
    >
      <Container>
        <Grid container spacing={5} alignItems="end" sx={{ mb: 5 }}>
          <Grid item xs={12} md={7}>
            <MKBox
              className="ro-mono"
              sx={{
                fontSize: 12,
                color: muted,
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                mb: 2,
              }}
            >
              ◦ Included free with every plan
            </MKBox>
            <MKTypography
              component="h2"
              sx={{
                m: 0,
                fontSize: { xs: "2.2rem", md: "4.5rem" },
                lineHeight: 0.95,
                letterSpacing: "-0.03em",
                fontWeight: 800,
                color: brandPrimary,
              }}
            >
              Your own booking page.{" "}
              <MKBox component="span" className="ro-serif" sx={{ fontStyle: "italic" }}>
                On us.
              </MKBox>
            </MKTypography>
          </Grid>
          <Grid item xs={12} md={5}>
            <MKTypography
              sx={{
                color: muted,
                fontSize: { xs: "1rem", md: "1.1rem" },
                lineHeight: 1.55,
                maxWidth: 420,
              }}
            >
              Every plan includes a public webpage for your business — hours, services, and contact
              info, live the moment your number is assigned. No web developer, no hosting bill.
            </MKTypography>
          </Grid>
        </Grid>

        {/* URL pill */}
        <MKBox
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1.25,
            px: 2.5,
            py: 1.25,
            borderRadius: 999,
            background: brandCard,
            border: `1px solid ${line}`,
            boxShadow: "0 10px 24px rgba(14,13,11,0.06)",
            mb: 5,
          }}
        >
          <MKBox
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: brandAccent,
              boxShadow: "0 0 0 4px rgba(58,91,217,0.2)",
            }}
          />
          <MKTypography
            className="ro-mono"
            sx={{ fontSize: { xs: 12, md: 14 }, fontWeight: 600, color: brandPrimary }}
          >
            ringoperator.com/
            <MKBox component="span" sx={{ opacity: 0.5 }}>
              your-business
            </MKBox>
          </MKTypography>
        </MKBox>

        <Grid container spacing={2}>
          {items.map((f, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <MKBox
                sx={{
                  background: brandCard,
                  border: `1px solid ${line}`,
                  borderRadius: 3,
                  p: 3.5,
                  minHeight: 220,
                  height: "100%",
                  transition: "all 200ms ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 14px 28px rgba(14,13,11,0.08)",
                  },
                }}
              >
                <MKBox sx={{ fontSize: 32, mb: 2.5 }}>{f.emoji}</MKBox>
                <MKTypography
                  component="h3"
                  sx={{
                    m: 0,
                    fontSize: "1.15rem",
                    fontWeight: 700,
                    letterSpacing: "-0.01em",
                    color: brandPrimary,
                  }}
                >
                  {f.title}
                </MKTypography>
                <MKTypography sx={{ color: muted, fontSize: "0.95rem", lineHeight: 1.55, mt: 1 }}>
                  {f.body}
                </MKTypography>
              </MKBox>
            </Grid>
          ))}
        </Grid>

        <MKTypography
          className="ro-mono"
          sx={{
            textAlign: "center",
            fontSize: 12,
            color: muted,
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            mt: 5,
          }}
        >
          turn booking, waitlist & feedback on or off from your dashboard
        </MKTypography>
      </Container>
    </MKBox>
  );
}

export default BusinessPage;
